export class Utils {
    /**
     * Generate a random room ID using cryptographically secure random values
     * @returns {string} 6-character alphanumeric room ID
     */
    static generateRoomId() {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        const randomValues = new Uint32Array(6);
        crypto.getRandomValues(randomValues);

        let result = '';
        for (let i = 0; i < 6; i++) {
            result += chars.charAt(randomValues[i] % chars.length);
        }
        return result;
    }

    /**
     * Generate a human-readable room name
     * @returns {string} Room name like "cozy-meeting-123"
     */
    static generateRoomName() {
        const words = [
            'quick', 'fast', 'easy', 'cool', 'nice', 'fun', 'good', 'best',
            'chat', 'call', 'room', 'meet', 'talk', 'zoom', 'team', 'work'
        ];

        const randomValues = new Uint32Array(2);
        crypto.getRandomValues(randomValues);

        const word1 = words[randomValues[0] % words.length];
        const word2 = words[randomValues[1] % words.length];
        const number = (randomValues[0] % 99) + 1;

        return `${word1}-${word2}-${number}`;
    }

    /**
     * Convert room name to URL-friendly format
     * @param {string} roomName
     * @returns {string}
     */
    static slugifyRoomName(roomName) {
        return roomName
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim('-');
    }

    /**
     * Validate room name format
     * @param {string} roomName
     * @returns {boolean}
     */
    static isValidRoomName(roomName) {
        // Room names should be more descriptive and not just random words
        // Must contain at least one space or hyphen to separate words
        // Must be 3-50 characters
        return /^[a-zA-Z0-9\s-]{3,50}$/.test(roomName) &&
               (roomName.includes(' ') || roomName.includes('-')) &&
               roomName.length >= 5; // Minimum length to ensure descriptive names
    }

    /**
     * Validate room ID format (legacy)
     * @param {string} roomId
     * @returns {boolean}
     */
    static isValidRoomId(roomId) {
        return /^[A-Z0-9]{6}$/.test(roomId);
    }

    /**
     * Check if input is a room name or room ID
     * @param {string} input
     * @returns {string} 'name' or 'id'
     */
    static getRoomInputType(input) {
        if (this.isValidRoomName(input)) {
            return 'name';
        } else if (this.isValidRoomId(input)) {
            return 'id';
        }
        return null;
    }

    /**
     * Generate mobile-optimized video constraints
     * @returns {object}
     */
    static getMobileVideoConstraints() {
        const isMobile = this.isMobile();
        const isIOS = this.isIOS();
        const isAndroid = this.isAndroid();
        const isLowEndDevice = this.isLowEndDevice();

        if (isMobile) {
            // Base mobile constraints
            let constraints = {
                width: { ideal: 640, max: 1280 },
                height: { ideal: 480, max: 720 },
                frameRate: { ideal: 24, max: 30 },
                facingMode: 'user'
            };

            // iOS Safari specific optimizations
            if (isIOS) {
                constraints = {
                    ...constraints,
                    width: { ideal: 480, max: 640 },
                    height: { ideal: 360, max: 480 },
                    frameRate: { ideal: 20, max: 24 }
                };
            }

            // Android specific optimizations
            if (isAndroid) {
                constraints = {
                    ...constraints,
                    width: { ideal: 640, max: 1280 },
                    height: { ideal: 480, max: 720 }
                };
            }

            // Low-end device optimizations
            if (isLowEndDevice) {
                constraints = {
                    ...constraints,
                    width: { ideal: 320, max: 480 },
                    height: { ideal: 240, max: 360 },
                    frameRate: { ideal: 15, max: 20 }
                };
            }

            return constraints;
        }

        return {
            width: { ideal: 1280, max: 1920 },
            height: { ideal: 720, max: 1080 },
            frameRate: { ideal: 30, max: 60 },
            facingMode: 'user'
        };
    }

    /**
     * Generate mobile-optimized audio constraints
     * @returns {object}
     */
    static getMobileAudioConstraints() {
        const isMobile = this.isMobile();
        const isIOS = this.isIOS();
        const isLowEndDevice = this.isLowEndDevice();

        let constraints = {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
        };

        if (isMobile) {
            constraints = {
                ...constraints,
                sampleRate: 16000,
                channelCount: 1
            };

            // iOS specific audio optimizations
            if (isIOS) {
                constraints = {
                    ...constraints,
                    sampleRate: 22050, // Better iOS compatibility
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: false // iOS handles this better
                };
            }

            // Low-end device optimizations
            if (isLowEndDevice) {
                constraints = {
                    ...constraints,
                    sampleRate: 8000, // Lower quality for better performance
                    channelCount: 1
                };
            }
        }

        return constraints;
    }

    /**
     * Format error messages for display
     * @param {Error} error
     * @returns {string}
     */
    static formatError(error) {
        const isMobile = this.isMobile();
        const isIOS = this.isIOS();

        if (error.name === 'NotAllowedError') {
            if (isMobile) {
                return 'Camera/microphone access denied. Please go to Settings > Privacy & Security > Camera/Microphone and allow access, then refresh the page.';
            }
            return 'Camera/microphone access denied. Please allow access and try again.';
        } else if (error.name === 'NotFoundError') {
            if (isMobile) {
                return 'No camera or microphone found. Please check your device settings and ensure the camera/microphone is enabled.';
            }
            return 'No camera or microphone found. Please connect a device and try again.';
        } else if (error.name === 'OverconstrainedError') {
            if (isMobile) {
                return 'Camera/microphone settings not supported on this device. Trying with mobile-optimized settings...';
            }
            return 'Camera/microphone constraints not supported. Trying with fallback settings...';
        } else if (error.name === 'NotReadableError') {
            if (isMobile) {
                return 'Camera/microphone is being used by another app. Please close other camera apps and try again.';
            }
            return 'Camera/microphone is being used by another application.';
        } else if (error.name === 'AbortError') {
            return 'Camera/microphone access was aborted.';
        } else if (error.name === 'NotSupportedError') {
            if (isIOS) {
                return 'WebRTC not supported in this browser. Please use Safari or Chrome on iOS.';
            }
            return 'WebRTC not supported in this browser. Please use a modern browser.';
        } else {
            return error.message || 'An unexpected error occurred.';
        }
    }

    /**
     * Detect mobile device
     * @returns {boolean}
     */
    static isMobile() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }

    /**
     * Detect iOS device
     * @returns {boolean}
     */
    static isIOS() {
        return /iPad|iPhone|iPod/.test(navigator.userAgent);
    }

    /**
     * Detect Safari browser
     * @returns {boolean}
     */
    static isSafari() {
        return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    }

    /**
     * Detect Android device
     * @returns {boolean}
     */
    static isAndroid() {
        return /Android/i.test(navigator.userAgent);
    }

    /**
     * Detect low-end mobile device
     * @returns {boolean}
     */
    static isLowEndDevice() {
        const isMobile = this.isMobile();
        if (!isMobile) return false;

        // Check for low-end indicators
        const hasLowMemory = navigator.deviceMemory && navigator.deviceMemory < 4;
        const hasLowCores = navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4;
        const isOldDevice = this.isOldDevice();

        return hasLowMemory || hasLowCores || isOldDevice;
    }

    /**
     * Detect old mobile device
     * @returns {boolean}
     */
    static isOldDevice() {
        const isMobile = this.isMobile();
        if (!isMobile) return false;

        // Check for old device indicators
        const ua = navigator.userAgent;
        const isOldIOS = /OS [1-9]_/i.test(ua) || /OS 1[0-2]_/i.test(ua);
        const isOldAndroid = /Android [1-6]/i.test(ua) || /Android [1-9]\./i.test(ua);

        return isOldIOS || isOldAndroid;
    }

    /**
     * Check WebRTC support
     * @returns {boolean}
     */
    static isWebRTCSupported() {
        return !!(window.RTCPeerConnection && navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    }

    /**
     * Get mobile-optimized WebRTC configuration
     * @returns {object}
     */
    static getMobileWebRTCConfig() {
        const isMobile = this.isMobile();
        const isLowEndDevice = this.isLowEndDevice();

        let config = {
            iceServers: [
                { urls: 'stun:stun.l.google.com:19302' },
                { urls: 'stun:stun1.l.google.com:19302' }
            ],
            iceCandidatePoolSize: 10
        };

        if (isMobile) {
            config = {
                ...config,
                // Mobile-specific optimizations
                bundlePolicy: 'max-bundle',
                rtcpMuxPolicy: 'require',
                iceTransportPolicy: 'all',
                // Enable ICE restart for better mobile connectivity
                iceRestart: true
            };

            // Low-end device optimizations
            if (isLowEndDevice) {
                config = {
                    ...config,
                    iceCandidatePoolSize: 5, // Reduce memory usage
                    // More aggressive bundling for low-end devices
                    bundlePolicy: 'max-bundle'
                };
            }
        }

        return config;
    }

    /**
     * Get browser info
     * @returns {object}
     */
    static getBrowserInfo() {
        const ua = navigator.userAgent;
        let browser = 'Unknown';
        let version = 'Unknown';

        if (ua.includes('Chrome') && !ua.includes('Edg')) {
            browser = 'Chrome';
            version = ua.match(/Chrome\/(\d+)/)?.[1] || 'Unknown';
        } else if (ua.includes('Firefox')) {
            browser = 'Firefox';
            version = ua.match(/Firefox\/(\d+)/)?.[1] || 'Unknown';
        } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
            browser = 'Safari';
            version = ua.match(/Version\/(\d+)/)?.[1] || 'Unknown';
        } else if (ua.includes('Edg')) {
            browser = 'Edge';
            version = ua.match(/Edg\/(\d+)/)?.[1] || 'Unknown';
        }

        return { browser, version };
    }

    /**
     * Debounce function
     * @param {Function} func
     * @param {number} wait
     * @returns {Function}
     */
    static debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    /**
     * Throttle function
     * @param {Function} func
     * @param {number} limit
     * @returns {Function}
     */
    static throttle(func, limit) {
        let inThrottle;
        return function executedFunction(...args) {
            if (!inThrottle) {
                func.apply(this, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    }

    /**
     * Get mobile performance metrics
     * @returns {object}
     */
    static getMobilePerformanceMetrics() {
        const isMobile = this.isMobile();
        if (!isMobile) return null;

        const metrics = {
            deviceMemory: navigator.deviceMemory || 'Unknown',
            hardwareConcurrency: navigator.hardwareConcurrency || 'Unknown',
            connection: 'Unknown',
            batteryLevel: 'Unknown'
        };

        // Network connection info
        if (navigator.connection) {
            metrics.connection = {
                effectiveType: navigator.connection.effectiveType,
                downlink: navigator.connection.downlink,
                rtt: navigator.connection.rtt
            };
        }

        // Battery info (if available)
        if (navigator.getBattery) {
            navigator.getBattery().then(battery => {
                metrics.batteryLevel = Math.round(battery.level * 100);
            }).catch(() => {
                metrics.batteryLevel = 'Unknown';
            });
        }

        return metrics;
    }

    /**
     * Check if device supports advanced features
     * @returns {object}
     */
    static getDeviceCapabilities() {
        const isMobile = this.isMobile();
        const isIOS = this.isIOS();
        const isAndroid = this.isAndroid();

        return {
            isMobile,
            isIOS,
            isAndroid,
            supportsWebRTC: this.isWebRTCSupported(),
            supportsMediaDevices: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
            supportsClipboard: !!(navigator.clipboard && navigator.clipboard.writeText),
            supportsTouch: 'ontouchstart' in window,
            supportsOrientation: 'onorientationchange' in window,
            supportsVibration: 'vibrate' in navigator,
            supportsGeolocation: 'geolocation' in navigator
        };
    }

    /**
     * Copy text to clipboard
     * @param {string} text
     * @returns {Promise<boolean>}
     */
    static async copyToClipboard(text) {
        try {
            if (navigator.clipboard) {
                await navigator.clipboard.writeText(text);
                return true;
            } else {
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = text;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                textArea.style.top = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                const result = document.execCommand('copy');
                document.body.removeChild(textArea);
                return result;
            }
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
            return false;
        }
    }

    /**
     * Format file size
     * @param {number} bytes
     * @returns {string}
     */
    static formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    /**
     * Format duration in seconds to human readable format
     * @param {number} seconds
     * @returns {string}
     */
    static formatDuration(seconds) {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);

        if (hours > 0) {
            return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        } else {
            return `${minutes}:${secs.toString().padStart(2, '0')}`;
        }
    }

    /**
     * Generate a UUID v4 using cryptographically secure random values
     * @returns {string}
     */
    static generateUUID() {
        const randomValues = new Uint8Array(16);
        crypto.getRandomValues(randomValues);

        // Set version (4) and variant bits
        randomValues[6] = (randomValues[6] & 0x0f) | 0x40;
        randomValues[8] = (randomValues[8] & 0x3f) | 0x80;

        const hex = Array.from(randomValues)
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');

        return [
            hex.substring(0, 8),
            hex.substring(8, 12),
            hex.substring(12, 16),
            hex.substring(16, 20),
            hex.substring(20, 32)
        ].join('-');
    }

    /**
     * Log levels for production/development control
     */
    static LOG_LEVELS = {
        DEBUG: 0,
        INFO: 1,
        WARN: 2,
        ERROR: 3
    };

    /**
     * Get current log level based on environment
     * @returns {number}
     */
    static getCurrentLogLevel() {
        // In production, only show WARN and ERROR
        // In development, show all levels
        return this.isDevelopment() ? this.LOG_LEVELS.DEBUG : this.LOG_LEVELS.WARN;
    }

    /**
     * Log with timestamp and level control
     * @param {number} level - Log level (DEBUG, INFO, WARN, ERROR)
     * @param {string} message
     * @param {any} data
     */
    static log(level, message, data = null) {
        if (level >= this.getCurrentLogLevel()) {
            const timestamp = new Date().toISOString();
            const levelName = Object.keys(this.LOG_LEVELS)[level] || 'UNKNOWN';

            if (data) {
                console.log(`[${timestamp}] [${levelName}] ${message}`, data);
            } else {
                console.log(`[${timestamp}] [${levelName}] ${message}`);
            }
        }
    }

    /**
     * Debug logging (only in development)
     * @param {string} message
     * @param {any} data
     */
    static debug(message, data = null) {
        this.log(this.LOG_LEVELS.DEBUG, message, data);
    }

    /**
     * Info logging
     * @param {string} message
     * @param {any} data
     */
    static info(message, data = null) {
        this.log(this.LOG_LEVELS.INFO, message, data);
    }

    /**
     * Warning logging
     * @param {string} message
     * @param {any} data
     */
    static warn(message, data = null) {
        this.log(this.LOG_LEVELS.WARN, message, data);
    }

    /**
     * Error logging
     * @param {string} message
     * @param {any} data
     */
    static error(message, data = null) {
        this.log(this.LOG_LEVELS.ERROR, message, data);
    }

    /**
     * Check if running in development mode
     * @returns {boolean}
     */
    static isDevelopment() {
        return window.location.hostname === 'localhost' ||
               window.location.hostname === '127.0.0.1' ||
               window.location.hostname === '';
    }

    /**
     * Get query parameter value
     * @param {string} param
     * @returns {string|null}
     */
    static getQueryParam(param) {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(param);
    }

    /**
     * Set query parameter without page reload
     * @param {string} param
     * @param {string} value
     */
    static setQueryParam(param, value) {
        const url = new URL(window.location);
        url.searchParams.set(param, value);
        window.history.pushState({}, '', url);
    }

    /**
     * Remove query parameter without page reload
     * @param {string} param
     */
    static removeQueryParam(param) {
        const url = new URL(window.location);
        url.searchParams.delete(param);
        window.history.pushState({}, '', url);
    }
}
